import { useCallback, useEffect, useRef, useState } from 'react';
import cloud from 'd3-cloud';
import { rectSpriteCanvas, glyphSpriteCanvas } from './spriteCanvas';
import { WORD_STYLE_CONFIG, WORD_SPREAD_CONFIG } from './wordCloudStyles';

// Layout always runs against this fixed canonical size (not the container's
// actual pixel size) so different windows showing the same cloud compute the
// identical relative layout, scaled to fit - same technique PresentationView
// uses for slides.
export const CANONICAL_WIDTH = 1600;
export const CANONICAL_HEIGHT = 900;

// Feeds d3-cloud's random() a word's last placed position (inverse of its
// own placement formula) so an unchanged word starts its search from where
// it already is instead of a fresh random point - keeps the cloud from
// reshuffling on every update.
function buildAnchoredRandom(input, positions, size) {
  const seq = [];
  input.forEach((d) => {
    const prev = positions.get(d.text);
    if (!prev) { seq.push(null, null); return; }
    seq.push(((prev.x + size[0] / 2) * 2) / size[0] - 0.5, ((prev.y + size[1] / 2) * 2) / size[1] - 0.5);
  });
  let i = 0;
  return () => {
    const v = seq[i];
    i += 1;
    return v === null || v === undefined ? Math.random() : v;
  };
}

// Cap on the uniform font zoom so a tiny cluster doesn't blow up to huge
// glyphs. Kept the same across spread levels so switching preset never
// changes text size.
const FONT_FILL_CAP = 2.2;
// Cap on the per-axis position stretch so a near-1D layout can't fling words
// absurdly far.
const POS_STRETCH_CAP = 8;
// Fixed margin the FONT zoom fits to - held constant (not per-mode) so text
// size never changes when you switch spread level.
const FONT_FILL_MARGIN = 0.96;

// Splits "fill the container" into two independent knobs:
//  - fontScale: a UNIFORM zoom (min-fit of the packed bounding box, at a
//    fixed margin) used only for glyph size, so text size is identical across
//    spread presets.
//  - stretchX/stretchY: PER-AXIS multipliers applied to word POSITIONS only.
//    axisFill scales how far they push (0 = leave the packed shape alone,
//    'compact'); edgeMargin sets how close to the boundary the outermost
//    words land (1 = word edges touch the container edge, 'spread'; <1 leaves
//    an inset gap, 'normal'). Because it only moves positions apart without
//    touching glyphs, words spread to the edges without shrinking or
//    distorting, and moving apart can never introduce overlap.
function computeFill(placed, axisFill, edgeMargin) {
  let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
  placed.forEach((w) => {
    minX = Math.min(minX, w.x - w.width / 2);
    maxX = Math.max(maxX, w.x + w.width / 2);
    minY = Math.min(minY, w.y - w.height / 2);
    maxY = Math.max(maxY, w.y + w.height / 2);
  });
  const halfW = Math.max(Math.abs(minX), Math.abs(maxX), 1);
  const halfH = Math.max(Math.abs(minY), Math.abs(maxY), 1);
  const fontWidthFill = ((CANONICAL_WIDTH / 2) / halfW) * FONT_FILL_MARGIN;
  const fontHeightFill = ((CANONICAL_HEIGHT / 2) / halfH) * FONT_FILL_MARGIN;
  const fontScale = Math.max(1, Math.min(FONT_FILL_CAP, Math.min(fontWidthFill, fontHeightFill)));
  const widthFill = ((CANONICAL_WIDTH / 2) / halfW) * edgeMargin;
  const heightFill = ((CANONICAL_HEIGHT / 2) / halfH) * edgeMargin;
  const targetX = Math.max(fontScale, Math.min(POS_STRETCH_CAP, widthFill));
  const targetY = Math.max(fontScale, Math.min(POS_STRETCH_CAP, heightFill));
  return {
    fontScale,
    stretchX: 1 + ((targetX / fontScale) - 1) * axisFill,
    stretchY: 1 + ((targetY / fontScale) - 1) * axisFill
  };
}

export default function useWordCloudLayout({
  words, hiddenWords, fontFamily, displayStyle, spread
}) {
  const styleConfig = WORD_STYLE_CONFIG[displayStyle] || WORD_STYLE_CONFIG.chips;
  const spreadConfig = WORD_SPREAD_CONFIG[spread] || WORD_SPREAD_CONFIG.normal;

  const containerRef = useRef(null);
  const layoutRunRef = useRef(0);
  const activeCloudRef = useRef(null);
  const debounceRef = useRef(null);
  const positionsRef = useRef(new Map());
  // Signature of the last word list actually laid out - callers (Firestore
  // onSnapshot in particular) rebuild the data array on every doc write even
  // when the words/votes are unchanged, so skip the expensive d3-cloud pass
  // when nothing meaningful actually changed.
  const lastLayoutKeyRef = useRef('');
  const [scaleToFit, setScaleToFit] = useState(1);
  const [contentScale, setContentScale] = useState(1);
  const [stretchX, setStretchX] = useState(1);
  const [stretchY, setStretchY] = useState(1);
  const [placedWords, setPlacedWords] = useState([]);

  const runLayoutNow = useCallback(() => {
    const visible = words.filter((w) => w?.name && !hiddenWords.has(w.name));

    if (!visible.length) {
      if (lastLayoutKeyRef.current === '') return;
      lastLayoutKeyRef.current = '';
      layoutRunRef.current += 1;
      activeCloudRef.current?.stop();
      setPlacedWords([]);
      setContentScale(1);
      setStretchX(1);
      setStretchY(1);
      return;
    }

    // Merge duplicate names (backend occasionally sends the same word
    // twice) - duplicates would break React key reconciliation.
    const mergedVotes = new Map();
    visible.forEach((w) => mergedVotes.set(w.name, (mergedVotes.get(w.name) || 0) + (w.weight || 1)));

    const layoutKey = `${displayStyle}|${spread}|${fontFamily}|${Array.from(mergedVotes.entries())
      .map(([name, votes]) => `${name}:${votes}`).sort().join(',')}`;
    if (layoutKey === lastLayoutKeyRef.current) return;
    lastLayoutKeyRef.current = layoutKey;

    const runId = layoutRunRef.current + 1;
    layoutRunRef.current = runId;
    activeCloudRef.current?.stop();

    const maxWeight = Math.max(...mergedVotes.values());

    // d3-cloud drops words it can't fit rather than scaling the whole cloud
    // down, so retry with smaller fonts until everything fits.
    const attempt = (scale) => {
      const minFont = Math.max(18, 22 * scale);
      const maxFont = Math.max(minFont + 10, 170 * scale);

      // Vote count is `votes`, not `weight` - d3-cloud owns `weight` as its
      // font-weight accessor output. Sorting here must match d3-cloud's own
      // internal size sort, or the anchored position queue goes to the
      // wrong words.
      const input = Array.from(mergedVotes.entries())
        .map(([text, votes]) => ({
          text,
          votes,
          fontSizeHint: Math.round(minFont + (maxFont - minFont) * ((votes / maxWeight) ** 0.9))
        }))
        .sort((a, b) => b.fontSizeHint - a.fontSizeHint);

      const size = [CANONICAL_WIDTH, CANONICAL_HEIGHT];
      const anchoredRandom = buildAnchoredRandom(input, positionsRef.current, size);

      const layout = cloud()
        .canvas(styleConfig.useRectCollision ? () => rectSpriteCanvas(spreadConfig.paddingScale) : glyphSpriteCanvas)
        .random(anchoredRandom)
        .size(size)
        .spiral(spreadConfig.spiral)
        .words(input)
        .font(fontFamily)
        .fontWeight(900)
        .fontSize((d) => d.fontSizeHint)
        // Rect collision already reserves chip padding (scaled by spread);
        // glyph collision uses a few base px, also scaled by spread.
        .padding(styleConfig.useRectCollision ? 0 : 3 * spreadConfig.paddingScale)
        .rotate((d) => styleConfig.rotate(d.text))
        .on('end', (placed) => {
          if (runId !== layoutRunRef.current) return; // superseded by a newer run
          if (placed.length < input.length && scale > 0.45) {
            attempt(scale * 0.85);
            return;
          }
          positionsRef.current = new Map(placed.map((w) => [w.text, { x: w.x, y: w.y }]));
          setPlacedWords(placed);
          // d3-cloud doesn't grow words to fill unused canvas space, so
          // zoom glyphs to fill (fontScale) and stretch positions per-axis
          // toward the edges by the chosen spread level.
          const fill = computeFill(placed, spreadConfig.axisFill, spreadConfig.edgeMargin);
          setContentScale(fill.fontScale);
          setStretchX(fill.stretchX);
          setStretchY(fill.stretchY);
        });

      activeCloudRef.current = layout;
      layout.start();
    };

    attempt(1);
  }, [words, hiddenWords, fontFamily, displayStyle, styleConfig, spread, spreadConfig]);

  // Debounced so bursts of updates (e.g. several Firestore snapshots in a
  // row) batch into one relayout instead of reflowing on every single one.
  const runLayout = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runLayoutNow, 350);
  }, [runLayoutNow]);

  useEffect(() => {
    runLayout();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [runLayout]);

  // runLayoutNow's identity changes on every word-data update; read it via a
  // ref here so the observer itself is only ever set up once per mount
  // instead of being torn down and recreated on every incoming word.
  const runLayoutNowRef = useRef(runLayoutNow);
  runLayoutNowRef.current = runLayoutNow;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const updateScale = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      setScaleToFit(Math.min(w / CANONICAL_WIDTH, h / CANONICAL_HEIGHT));
    };
    const observer = new ResizeObserver(updateScale);
    observer.observe(el);
    updateScale();
    // Relayout once the real font is loaded (avoids sizing against a fallback).
    document.fonts?.ready?.then(() => runLayoutNowRef.current()).catch(() => { });
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    layoutRunRef.current += 1;
    activeCloudRef.current?.stop();
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return {
    containerRef, placedWords, scaleToFit, contentScale, stretchX, stretchY, styleConfig
  };
}
